import type { SupabaseClient, User } from "@supabase/supabase-js";
import { withMonitoringSpan } from "./monitoring";
import { runtimeEnv } from "./runtimeEnv";

const supabaseUrl = runtimeEnv.VITE_SUPABASE_URL?.trim();
const supabasePublishableKey =
  runtimeEnv.VITE_SUPABASE_PUBLISHABLE_KEY?.trim() ??
  runtimeEnv.VITE_SUPABASE_ANON_KEY?.trim();
const AUTH_DEBUG_ENABLED = runtimeEnv.DEV === true;

let supabaseClientPromise: Promise<SupabaseClient | null> | null = null;
let ensureAnonymousSessionPromise: Promise<User | null> | null = null;
let lastAnonymousAuthErrorMessage: string | null = null;

type SupabaseFunctionErrorBody = Partial<{
  error: unknown;
  details: unknown;
  message: unknown;
}>;
type SupabaseAuthSessionData = Partial<{
  session: Partial<{
    access_token: string | null;
    user: User | null;
  }> | null;
  user: User | null;
}>;
type SupabaseAuthResponseLike = {
  data: SupabaseAuthSessionData;
  error: unknown;
};

export type AnonymousSessionRecoveryClient = Pick<SupabaseClient, "auth">;

export const supabaseConfigError =
  supabaseUrl && supabasePublishableKey
    ? null
    : "Leaderboard unavailable: Supabase environment variables are missing.";

function setAnonymousAuthErrorMessage(message: string | null) {
  lastAnonymousAuthErrorMessage = message;
}

function logAuthDebug(message: string, details?: Record<string, unknown>) {
  if (!AUTH_DEBUG_ENABLED) {
    return;
  }

  if (details === undefined) {
    console.info(`[supabase-auth] ${message}`);
    return;
  }

  console.info(`[supabase-auth] ${message}`, details);
}

function sanitizeFunctionErrorPart(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim();
  return normalizedValue.length > 0 ? normalizedValue : null;
}

function getUnknownErrorMessage(error: unknown): string | null {
  if (error instanceof Error) {
    return sanitizeFunctionErrorPart(error.message);
  }

  if (typeof error === "object" && error !== null && "message" in error) {
    return sanitizeFunctionErrorPart((error as { message?: unknown }).message);
  }

  return null;
}

function getAuthResponseUser(data: SupabaseAuthSessionData): User | null {
  return data.user ?? data.session?.user ?? null;
}

function getAuthResponseAccessToken(data: SupabaseAuthSessionData): string | null {
  const accessToken = data.session?.access_token;
  return typeof accessToken === "string" && accessToken.trim().length > 0 ? accessToken : null;
}

function buildFunctionErrorMessage(
  primaryMessage: string | null,
  secondaryMessage: string | null,
  fallbackMessage: string
): string {
  if (primaryMessage === null) {
    return secondaryMessage ?? fallbackMessage;
  }

  if (secondaryMessage === null || secondaryMessage === primaryMessage) {
    return primaryMessage;
  }

  return `${primaryMessage} ${secondaryMessage}`;
}

function getFunctionErrorResponse(error: unknown): Response | null {
  if (typeof Response === "undefined" || typeof error !== "object" || error === null) {
    return null;
  }

  const context =
    "context" in error
      ? (error as { context?: unknown }).context
      : "response" in error
        ? (error as { response?: unknown }).response
        : null;

  return context instanceof Response ? context : null;
}

export async function getSupabaseFunctionErrorMessage(
  error: unknown,
  fallbackMessage: string
): Promise<string> {
  const response = getFunctionErrorResponse(error);
  if (response === null) {
    if (error instanceof Error && error.message.trim().length > 0) {
      return error.message;
    }

    return fallbackMessage;
  }

  try {
    const responseClone = response.clone();
    const contentType = responseClone.headers.get("Content-Type")?.split(";")[0].trim();

    if (contentType === "application/json") {
      const payload = (await responseClone.json()) as SupabaseFunctionErrorBody;
      return buildFunctionErrorMessage(
        sanitizeFunctionErrorPart(payload.error) ?? sanitizeFunctionErrorPart(payload.message),
        sanitizeFunctionErrorPart(payload.details),
        fallbackMessage
      );
    }

    return sanitizeFunctionErrorPart(await responseClone.text()) ?? fallbackMessage;
  } catch {
    return fallbackMessage;
  }
}

export function isInvalidJwtErrorMessage(message: string | null | undefined): boolean {
  return typeof message === "string" && /\binvalid jwt\b/i.test(message);
}

async function createSupabaseClient(): Promise<SupabaseClient | null> {
  if (!supabaseUrl || !supabasePublishableKey) {
    return null;
  }

  const { createClient } = await import("@supabase/supabase-js");

  return createClient(supabaseUrl, supabasePublishableKey, {
    auth: {
      autoRefreshToken: true,
      detectSessionInUrl: false,
      persistSession: true
    }
  });
}

export async function getSupabaseClient(): Promise<SupabaseClient | null> {
  if (supabaseConfigError !== null) {
    return null;
  }

  if (supabaseClientPromise === null) {
    supabaseClientPromise = createSupabaseClient().catch((error: unknown) => {
      supabaseClientPromise = null;
      logAuthDebug("Supabase client creation failed.", {
        message: error instanceof Error ? error.message : "Unknown error"
      });
      return null;
    });
  }

  return supabaseClientPromise;
}

export function getAnonymousAuthErrorMessage(): string | null {
  return lastAnonymousAuthErrorMessage;
}

async function getSessionUser(client: SupabaseClient | null): Promise<User | null> {
  if (client === null) {
    return null;
  }

  const { data, error } = await client.auth.getSession();
  if (error) {
    logAuthDebug("Could not read existing session.", { message: error.message });
    return null;
  }

  return data.session?.user ?? null;
}

async function getSessionAccessToken(client: SupabaseClient | null): Promise<string | null> {
  if (client === null) {
    return null;
  }

  const { data, error } = await client.auth.getSession();
  if (error) {
    logAuthDebug("Could not read the current access token.", {
      message: error.message
    });
    return null;
  }

  return getAuthResponseAccessToken(data);
}

export async function recoverAnonymousSession(
  client: AnonymousSessionRecoveryClient
): Promise<User | null> {
  const refreshResult = (await client.auth.refreshSession()) as SupabaseAuthResponseLike;
  const refreshedUser = getAuthResponseUser(refreshResult.data);
  if (refreshedUser !== null) {
    setAnonymousAuthErrorMessage(null);
    logAuthDebug("Refreshed anonymous session after auth failure.", {
      userId: refreshedUser.id
    });
    return refreshedUser;
  }

  const refreshErrorMessage = getUnknownErrorMessage(refreshResult.error);
  if (refreshErrorMessage !== null) {
    setAnonymousAuthErrorMessage(refreshErrorMessage);
    logAuthDebug("Could not refresh anonymous session.", {
      message: refreshErrorMessage
    });
  }

  const signOutResult = await client.auth.signOut({ scope: "local" });
  const signOutErrorMessage = getUnknownErrorMessage(signOutResult.error);
  if (signOutErrorMessage !== null) {
    logAuthDebug("Could not clear the invalid anonymous session.", {
      message: signOutErrorMessage
    });
  }

  const signInResult = (await client.auth.signInAnonymously()) as SupabaseAuthResponseLike;
  const nextUser = getAuthResponseUser(signInResult.data);
  if (nextUser !== null) {
    setAnonymousAuthErrorMessage(null);
    logAuthDebug("Created a replacement anonymous session.", {
      userId: nextUser.id
    });
    return nextUser;
  }

  const signInErrorMessage =
    getUnknownErrorMessage(signInResult.error) ?? "Supabase did not return an anonymous user.";
  setAnonymousAuthErrorMessage(signInErrorMessage);
  logAuthDebug("Anonymous session recovery failed.", {
    message: signInErrorMessage
  });
  return null;
}

export async function ensureAnonymousSession(): Promise<User | null> {
  const client = await getSupabaseClient();
  if (client === null) {
    setAnonymousAuthErrorMessage(supabaseConfigError);
    return null;
  }

  if (ensureAnonymousSessionPromise !== null) {
    return ensureAnonymousSessionPromise;
  }

  ensureAnonymousSessionPromise = (async () => {
    const existingUser = await getSessionUser(client);
    if (existingUser !== null) {
      setAnonymousAuthErrorMessage(null);
      logAuthDebug("Reused anonymous session.", { userId: existingUser.id });
      return existingUser;
    }

    const { data, error } = await client.auth.signInAnonymously();
    if (error) {
      setAnonymousAuthErrorMessage(error.message);
      logAuthDebug("Anonymous sign-in failed.", { message: error.message });
      return null;
    }

    const nextUser = data.user ?? data.session?.user ?? null;
    if (nextUser !== null) {
      setAnonymousAuthErrorMessage(null);
      logAuthDebug("Created anonymous session.", { userId: nextUser.id });
      return nextUser;
    }

    setAnonymousAuthErrorMessage("Supabase did not return an anonymous user.");
    logAuthDebug("Anonymous sign-in completed without a user.");
    return null;
  })()
    .catch((error: unknown) => {
      setAnonymousAuthErrorMessage(error instanceof Error ? error.message : "Unknown error");
      logAuthDebug("Anonymous auth initialization failed.", {
        message: error instanceof Error ? error.message : "Unknown error"
      });
      return null;
    })
    .finally(() => {
      ensureAnonymousSessionPromise = null;
    });

  return ensureAnonymousSessionPromise;
}

export async function getCurrentUserId(): Promise<string | null> {
  const client = await getSupabaseClient();
  if (client === null) {
    setAnonymousAuthErrorMessage(supabaseConfigError);
    return null;
  }

  const { data, error } = await client.auth.getUser();
  if (error) {
    logAuthDebug("Could not verify auth user. Falling back to session user.", {
      message: error.message
    });
    const sessionUser = await getSessionUser(client);
    if (sessionUser === null) {
      setAnonymousAuthErrorMessage(error.message);
    }
    return sessionUser?.id ?? null;
  }

  setAnonymousAuthErrorMessage(null);
  return data.user?.id ?? null;
}

export async function invokeSupabaseFunction<TResponse>(
  functionName: string,
  body: Record<string, unknown>,
  options: Partial<{
    shouldTrace: boolean;
    spanName: string;
    spanOp: string;
  }> = {}
): Promise<TResponse> {
  const executeInvoke = async () => {
    const client = await getSupabaseClient();
    if (client === null) {
      throw new Error(supabaseConfigError ?? "Supabase is unavailable.");
    }

    const invokeWithCurrentSession = async () => {
      const accessToken = await getSessionAccessToken(client);
      return client.functions.invoke(functionName, {
        body,
        ...(accessToken === null
          ? {}
          : {
              headers: {
                Authorization: `Bearer ${accessToken}`
              }
            })
      });
    };

    const fallbackMessage = `Could not call ${functionName}.`;
    let { data, error } = await invokeWithCurrentSession();
    if (error) {
      let message = await getSupabaseFunctionErrorMessage(error, fallbackMessage);

      if (isInvalidJwtErrorMessage(message)) {
        logAuthDebug("Supabase function rejected the current JWT. Recovering session.", {
          functionName
        });

        const recoveredUser = await recoverAnonymousSession(client);
        if (recoveredUser !== null) {
          const retryResult = await invokeWithCurrentSession();
          data = retryResult.data;
          error = retryResult.error;

          if (!error) {
            return data as TResponse;
          }

          message = await getSupabaseFunctionErrorMessage(error, fallbackMessage);
        } else {
          message = getAnonymousAuthErrorMessage() ?? message;
        }
      }

      throw new Error(message);
    }

    return data as TResponse;
  };

  if (options.shouldTrace === false) {
    return executeInvoke();
  }

  return withMonitoringSpan(
    options.spanName ?? `supabase.function.${functionName}`,
    executeInvoke,
    {
      attributes: {
        "supabase.function": functionName
      },
      op: options.spanOp ?? "http.client"
    }
  );
}
